import { useState, useMemo } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ElectionResultWithDetails } from '@/types'

interface RegionalBreakdownProps {
  results: ElectionResultWithDetails[]
  className?: string
}

interface PartyInfo {
  id: string
  name: string
  abbreviation: string
  color: string
}

interface LocationResult {
  name: string
  id: string
  regionId?: string
  partyResults: Map<string, { vote_share: number; vote_count: number; seats_won: number }>
  totalVotes: number
  isCloseMargin: boolean
}

export function RegionalBreakdown({ results, className }: RegionalBreakdownProps) {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())

  // Extract unique parties from results
  const parties = useMemo<PartyInfo[]>(() => {
    const partyMap = new Map<string, PartyInfo>()
    for (const r of results) {
      if (r.party && !partyMap.has(r.party_id)) {
        partyMap.set(r.party_id, {
          id: r.party.id,
          name: r.party.name,
          abbreviation: r.party.abbreviation,
          color: r.party.color,
        })
      }
    }
    return Array.from(partyMap.values())
  }, [results])

  // Group results into regions and districts
  const { regions, districtsByRegion } = useMemo(() => {
    const regionResults = results.filter((r) => r.region_id && !r.district_id)
    const districtResults = results.filter((r) => r.district_id)

    const regionMap = new Map<string, LocationResult>()
    for (const r of regionResults) {
      const regionId = r.region_id!
      if (!regionMap.has(regionId)) {
        regionMap.set(regionId, {
          name: r.region?.name ?? 'Unknown Region',
          id: regionId,
          partyResults: new Map(),
          totalVotes: 0,
          isCloseMargin: false,
        })
      }
      const region = regionMap.get(regionId)!
      region.partyResults.set(r.party_id, {
        vote_share: r.vote_share,
        vote_count: r.vote_count,
        seats_won: r.seats_won,
      })
      region.totalVotes += r.vote_count
    }

    // Group districts by region
    const distByRegion = new Map<string, LocationResult[]>()
    const distMap = new Map<string, LocationResult>()
    for (const r of districtResults) {
      const districtId = r.district_id!
      const regionId = r.district?.region_id ?? 'unknown'
      if (!distMap.has(districtId)) {
        distMap.set(districtId, {
          name: r.district?.name ?? 'Unknown District',
          id: districtId,
          regionId,
          partyResults: new Map(),
          totalVotes: 0,
          isCloseMargin: false,
        })
      }
      const district = distMap.get(districtId)!
      district.partyResults.set(r.party_id, {
        vote_share: r.vote_share,
        vote_count: r.vote_count,
        seats_won: r.seats_won,
      })
      district.totalVotes += r.vote_count
    }

    // Organize districts by region and check close margins
    for (const district of distMap.values()) {
      const shares = Array.from(district.partyResults.values())
        .map((p) => p.vote_share)
        .sort((a, b) => b - a)
      if (shares.length >= 2 && shares[0] - shares[1] < 5) {
        district.isCloseMargin = true
      }

      const regionId = district.regionId ?? 'unknown'
      if (!distByRegion.has(regionId)) {
        distByRegion.set(regionId, [])
      }
      distByRegion.get(regionId)!.push(district)
    }

    // Sort districts within each region by name
    for (const districts of distByRegion.values()) {
      districts.sort((a, b) => a.name.localeCompare(b.name))
    }

    const regionList = Array.from(regionMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    return { regions: regionList, districtsByRegion: distByRegion }
  }, [results])

  const toggleRegion = (regionId: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev)
      if (next.has(regionId)) {
        next.delete(regionId)
      } else {
        next.add(regionId)
      }
      return next
    })
  }

  if (regions.length === 0) {
    return null
  }

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Regional Breakdown</CardTitle>
        <CardDescription>
          Click a region to see district-level results
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Location</TableHead>
              {parties.map((p) => (
                <TableHead key={p.id} className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: p.color }}
                    />
                    <span>{p.abbreviation}</span>
                  </div>
                </TableHead>
              ))}
              <TableHead className="text-right">Votes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {regions.map((region) => {
              const isExpanded = expandedRegions.has(region.id)
              const districts = districtsByRegion.get(region.id) ?? []

              return (
                <RegionRows
                  key={region.id}
                  region={region}
                  districts={districts}
                  parties={parties}
                  isExpanded={isExpanded}
                  onToggle={() => toggleRegion(region.id)}
                />
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

interface RegionRowsProps {
  region: LocationResult
  districts: LocationResult[]
  parties: PartyInfo[]
  isExpanded: boolean
  onToggle: () => void
}

function RegionRows({ region, districts, parties, isExpanded, onToggle }: RegionRowsProps) {
  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={onToggle}
      >
        <TableCell className="font-medium">
          <div className="flex items-center gap-1">
            {districts.length > 0 && (
              isExpanded
                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            {region.name}
          </div>
        </TableCell>
        {parties.map((p) => {
          const pr = region.partyResults.get(p.id)
          return (
            <TableCell key={p.id} className="text-center tabular-nums">
              {pr ? (
                <div>
                  <div className="text-sm">{pr.vote_share.toFixed(1)}%</div>
                  {pr.seats_won > 0 && (
                    <div className="text-xs text-muted-foreground">{pr.seats_won} seat{pr.seats_won !== 1 ? 's' : ''}</div>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
          )
        })}
        <TableCell className="text-right tabular-nums text-muted-foreground">
          {region.totalVotes.toLocaleString()}
        </TableCell>
      </TableRow>
      {isExpanded && districts.map((district) => (
        <TableRow key={district.id} className="bg-muted/30">
          <TableCell className="pl-10 text-sm">
            <div className="flex items-center gap-1.5">
              {district.name}
              {district.isCloseMargin && (
                <Badge variant="outline" className="text-xs py-0 px-1">Close</Badge>
              )}
            </div>
          </TableCell>
          {parties.map((p) => {
            const pr = district.partyResults.get(p.id)
            return (
              <TableCell key={p.id} className="text-center tabular-nums text-sm">
                {pr ? (
                  <span>{pr.vote_share.toFixed(1)}%</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            )
          })}
          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
            {district.totalVotes.toLocaleString()}
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
